import { getApiBaseUrl } from "@/lib/api-client/config";

/**
 * Per-deployment settings, fetched from the API at startup.
 *
 * The SPA is built once and the same static bundle is served to every
 * environment, so the API origin is the only value that can be baked in at
 * build time (see lib/api-client/config.ts). Everything that differs between
 * dev, staging and production — analytics credentials, limits, environment
 * name — is read from `GET /config/public` at runtime instead.
 */
/** The server's channel vocabulary (server/services/cli/releaseManifest.ts). */
export type CliReleaseChannel = "stable" | "dev";

export function isCliReleaseChannel(
  value: unknown,
): value is CliReleaseChannel {
  return value === "stable" || value === "dev";
}

export type PublicConfig = {
  environment?: string;
  /** The API's own build sha, which deploys separately from this bundle. */
  apiVersion?: string;
  /** The AI engine's build sha; a third image on a third tag. */
  aiVersion?: string;
  posthog?: {
    enabled: boolean;
    mode: "disabled" | "cloud" | "self-hosted";
    host?: string;
    apiKey?: string;
    /** Reverse proxy URL to bypass ad blockers. */
    proxyHost?: string;
  };
  faro?: {
    enabled: boolean;
    collectorUrl?: string;
    apiKey?: string;
  };
  /** Maximum upload file size in MB. */
  maxUploadSizeMB?: number;
  /** CUPID deployment URL; absent when CUPID is not deployed. */
  cupidUrl?: string;
  /** The CLI release channel this deployment answers for. */
  cliChannel?: CliReleaseChannel;
};

const EMPTY_CONFIG: PublicConfig = {};

let cachedConfig: PublicConfig = EMPTY_CONFIG;

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readPostHog(value: unknown): PublicConfig["posthog"] {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  const mode = asString(raw.mode);
  return {
    enabled: raw.enabled === true,
    mode:
      mode === "cloud" || mode === "self-hosted" || mode === "disabled"
        ? mode
        : "disabled",
    host: asString(raw.host),
    apiKey: asString(raw.apiKey),
    proxyHost: asString(raw.proxyHost),
  };
}

function readFaro(value: unknown): PublicConfig["faro"] {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  return {
    enabled: raw.enabled === true,
    collectorUrl: asString(raw.collectorUrl),
    apiKey: asString(raw.apiKey),
  };
}

function readPublicConfig(payload: unknown): PublicConfig {
  if (!payload || typeof payload !== "object") return EMPTY_CONFIG;
  const raw = payload as Record<string, unknown>;
  return {
    environment: asString(raw.environment),
    apiVersion: asString(raw.apiVersion),
    aiVersion: asString(raw.aiVersion),
    posthog: readPostHog(raw.posthog),
    faro: readFaro(raw.faro),
    maxUploadSizeMB:
      typeof raw.maxUploadSizeMB === "number" &&
      Number.isFinite(raw.maxUploadSizeMB)
        ? raw.maxUploadSizeMB
        : undefined,
    cupidUrl: asString(raw.cupidUrl),
    cliChannel: isCliReleaseChannel(raw.cliChannel)
      ? raw.cliChannel
      : undefined,
  };
}

/**
 * Fetches the public config and caches it for `getPublicConfig`. A failure is
 * not fatal: the app renders with an empty config and the features that depend
 * on it stay off.
 */
export async function loadPublicConfig(): Promise<PublicConfig> {
  const url = `${getApiBaseUrl()}/config/public`;
  try {
    const response = await fetch(url, { credentials: "omit" });
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    cachedConfig = readPublicConfig(await response.json());
  } catch (error) {
    console.warn(`[config] Could not load public config from ${url}`, error);
    cachedConfig = EMPTY_CONFIG;
  }
  return cachedConfig;
}

/**
 * The config loaded at startup. Empty until `loadPublicConfig` has settled, so
 * callers must treat every field as optional.
 */
export function getPublicConfig(): PublicConfig {
  return cachedConfig;
}

/**
 * The CLI release channel the download page shows.
 *
 * Stable when the config did not load, which is the direction the server fails
 * in too: withhold an internal build rather than offer one whose artifacts may
 * already have been pruned.
 */
export function getCliChannel(): CliReleaseChannel {
  return getPublicConfig().cliChannel ?? "stable";
}
